// src/components/InvestmenOption.js - VERSION CORRIGÉE ET COMPLÈTE

import React, { useState } from 'react';
import { Row, Col, Card, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCube, faMicrochip, faInfinity, faRobot } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom'; 
import InvestmentModal from './InvestmentModal';
import ReferralPromptModal from './ReferralPromptModal';
import axios from 'axios'; 

// Configuration de l'instance Axios
const api = axios.create({
    baseURL: 'https://api.blackroq.pro/api' // Assurez-vous que l'URL est correcte
});

// Fonction utilitaire pour formater les montants
const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF', 
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount);
};

// Fonction utilitaire : Extraire le code de parrainage
const extractCodeFromLink = (link) => {
    try {
        const url = new URL(link);
        // Cherche le paramètre 'referral' ou 'ref'
        const code = url.searchParams.get('referral') || url.searchParams.get('ref');
        return code || link; 
    } catch (e) {
        // Retourne le lien si ce n'est pas une URL valide (peut être juste le code)
        return link; 
    }
};

const investmentPackages = [
    { name: 'OMEGA AI', amounts: [25000, 50000, 75000], color: '#003366', icon: faCube, description: 'Le point de départ idéal pour tester la puissance de l\'IA.' },
    { name: 'G3N·3 AI', amounts: [100000, 250000, 500000], color: '#ff8c00', icon: faMicrochip, description: 'Performance et stabilité accrue pour les investisseurs sérieux.' },
    { name: 'INFINITY AI 6.0.2', amounts: [1000000, 2500000, 5000000], color: '#40E0D0', icon: faInfinity, description: 'L\'accès illimité à nos stratégies algorithmiques les plus avancées.' }
];

const InvestmentOptions = () => {
    const navigate = useNavigate(); 
    
    const [modalInfo, setModalInfo] = useState(null); // Stocke les données pour InvestmentModal
    const [showReferralPrompt, setShowReferralPrompt] = useState(false); // État pour ReferralPromptModal
    const [currentPackage, setCurrentPackage] = useState(null); // Le package sélectionné avant l'ouverture de la modale
    const [referralError, setReferralError] = useState(null);
    const [isCheckingReferral, setIsCheckingReferral] = useState(false);

    // Fonction utilitaire pour afficher InvestmentModal
    const openInvestmentModal = (referralCode = null) => {
        if (currentPackage) {
            setModalInfo({ 
                title: currentPackage.name, 
                amounts: currentPackage.amounts,
                color: currentPackage.color, 
                referralLink: referralCode // null si pas de lien
            });
        }
    };
    
    // Fonction pour fermer InvestmentModal
    const closeModal = () => {
        setModalInfo(null);
    };

    // Déclenché au clic sur "INVESTIR MAINTENANT"
    const handleInvestmentClick = (pkg) => {
        setReferralError(null); 
        setCurrentPackage(pkg);
        setShowReferralPrompt(true); // Ouvre ReferralPromptModal
    };

    // Déclenché au clic sur le bouton 'X' dans ReferralPromptModal
    const handleSimpleClose = () => {
        setShowReferralPrompt(false);
    };

    // 🌟 LOGIQUE CORRIGÉE : Gère le clic sur "Non, continuer sans lien"
    const handleProceedWithoutLinkAndShowModal = () => {
        setShowReferralPrompt(false); // 1. Ferme ReferralPromptModal
        openInvestmentModal(null);     // 2. Ouvre InvestmentModal (sans lien de parrainage)
    };


    // LOGIQUE CRITIQUE : Soumission du lien de parrainage
    const handleReferralLinkSubmit = async (fullLinkOrCode) => {
        setReferralError(null); 
        setIsCheckingReferral(true);

        const referralCode = extractCodeFromLink(fullLinkOrCode);

        if (!referralCode || referralCode.length < 5) {
            setIsCheckingReferral(false);
            setReferralError("Le code de parrainage est trop court ou manquant. Veuillez le vérifier.");
            return;
        }

        try {
            const res = await api.get(`/investment/check-referral?code=${referralCode}`); 

            console.log("✅ [InvestmentOptions] Réponse du backend (200) :", res.data);

            const { planName, amountInOriginalCurrency } = res.data;
            
            if (planName && amountInOriginalCurrency) {
                
                // ✅ SUCCÈS : Fermeture du prompt
                setShowReferralPrompt(false);
                
                // 🛑 REDIRECTION : L'utilisateur est envoyé directement vers la souscription
                navigate(
                    `/subscribe?plan=${encodeURIComponent(planName)}&amount=${amountInOriginalCurrency}&referral=${referralCode}`
                ); 
                
            } else {
                setReferralError("Le lien est valide, mais les données du plan sont incomplètes (Erreur de structure).");
            }

        } catch (error) {
            console.error("Referral check error:", error.response?.data || error.message);
            const msg = error.response?.data?.message || "Ce lien de parrainage est invalide ou n'existe pas.";
            setReferralError(msg);
        } finally {
            setIsCheckingReferral(false);
        }
    };


    return (
        <div className="investment-options-container">
            <Row className="justify-content-center g-4">
                {investmentPackages.map((pkg, index) => (
                    <Col md={4} key={index}>
                        <Card 
                            className="h-100 text-center ai-card-tech shadow" 
                            style={{ border: `2px solid ${pkg.color}80`, borderRadius: '12px', transition: 'all 0.3s', cursor: 'pointer', backgroundColor: '#ffffff' }}
                            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-5px)'}
                            onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                        >
                            <Card.Body>
                                <FontAwesomeIcon icon={pkg.icon || faRobot} size="3x" className="mb-3" style={{ color: pkg.color }} />
                                <Card.Title className="fw-bold" style={{ color: pkg.color }}>{pkg.name}</Card.Title>
                                <Card.Text className="text-muted small mb-3">{pkg.description}</Card.Text>
                                <div className="mb-3 p-2 rounded" style={{ backgroundColor: `${pkg.color}10`, border: `1px solid ${pkg.color}20` }}>
                                    <p className="mb-0 small fw-bold" style={{ color: pkg.color }}>Investissement à partir de :</p>
                                    <p className="lead fw-bold mb-0" style={{ color: pkg.color }}>{formatAmount(pkg.amounts[0])}</p>
                                </div>
                                <Button 
                                    variant="outline-secondary" 
                                    className="w-100 fw-bold"
                                    style={{ borderColor: pkg.color, color: pkg.color }}
                                    onClick={() => handleInvestmentClick(pkg)}
                                >
                                    INVESTIR MAINTENANT
                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* 1. InvestmentModal : S'affiche si modalInfo est défini (clic sur "sans lien") */}
            {modalInfo && (
                <InvestmentModal
                    title={modalInfo.title}
                    amounts={modalInfo.amounts}
                    onClose={closeModal}
                    referralLink={modalInfo.referralLink}
                    backgroundColor={modalInfo.color}
                />
            )}

            {/* 2. ReferralPromptModal : S'affiche au premier clic sur "INVESTIR MAINTENANT" */}
            {showReferralPrompt && (
                <ReferralPromptModal
                    onClose={handleSimpleClose}
                    onLinkSubmit={handleReferralLinkSubmit}
                    errorMessage={referralError} 
                    isLoading={isCheckingReferral}
                    // 🌟 Utilise la nouvelle fonction pour passer à InvestmentModal
                    onProceedWithoutLink={handleProceedWithoutLinkAndShowModal} 
                />
            )}
        </div>
    );
};

export default InvestmentOptions;